import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleLeft } from "@fortawesome/pro-solid-svg-icons/faAngleLeft";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Divider from "antd/es/divider";
import Typography from "antd/es/typography";
import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";

import ButtonLink from "@app/component/ButtonLink";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import Path from "@app/enum/Path";
import { hasPermission, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import MapFilter from "../../enums/MapFilter";

import styles from "./FilterItem.module.scss";

const { Text } = Typography;

interface Props {
  currentFilter?: MapFilter;
  filter: MapFilter;
  hasDownloadPermission?: boolean;
  isOwner: boolean;
  onClick?: (filter: MapFilter) => void;
  onDownloadClick?: (filter: MapFilter) => void;
  title: string;
}

const FilterItem: React.FC<Props> = ({
  currentFilter,
  filter,
  hasDownloadPermission,
  isOwner,
  onClick,
  onDownloadClick,
  title,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const active = filter === currentFilter;
  const canDownload = !!onDownloadClick && hasDownloadPermission;
  const handleClick = useCallback(() => onClick && onClick(filter), [filter, onClick]);
  const handleDownloadClick = useCallback(
    () => onDownloadClick && onDownloadClick(filter),
    [filter, onDownloadClick]
  );

  const info: React.ReactNode | undefined = useMemo(() => {
    if (filter === MapFilter.DEALS_ROTTING) {
      return (
        <div>
          <div>
            <Text strong>
              {intl.formatMessage({
                id: "mapFilter.dealsRotting.forOwner,info.title",
                defaultMessage: "Deals rotting",
                description: "Tooltip title for pins of entities with outOfCadence: true",
              })}
            </Text>
          </div>
          <Divider />
          <div>
            <Text>
              {!isOwner
                ? intl.formatMessage({
                    id: "mapFilter.dealsRotting.forOwner,info.text",
                    defaultMessage:
                      "Set rot limits for deal stages in Funnel Settings to be alerted when deals are in a given stage for too long",
                    description:
                      "Tooltip text for owner for pins of entities with outOfCadence: true",
                  })
                : intl.formatMessage(
                    {
                      id: "mapFilter.dealsRotting.forUser,info.text",
                      defaultMessage:
                        "Rot limits for deal stage are set by your organization administrator. View stage rot limits via <a>Deals Board View</a>.",
                      description:
                        "Tooltip title for user for pins of entities with outOfCadence: true",
                    },
                    {
                      a: (text) => (
                        <ButtonLink onClick={() => history.push(Path.DEAL)}>{text}</ButtonLink>
                      ),
                    }
                  )}
            </Text>
          </div>
        </div>
      );
    }
  }, [history, intl, isOwner, filter]);

  return (
    <div className={cn(styles.container, { [styles.active]: active })} onClick={handleClick}>
      <div className={styles.title}>
        {active && <FontAwesomeIcon className={styles.caret} icon={faAngleLeft} />}
        &nbsp;
        {info ? (
          <TextWithInfo iconProps={{ size: "xs" }} info={info} placement="bottom">
            {title}
          </TextWithInfo>
        ) : (
          title
        )}
      </div>
      {active && canDownload && <FontAwesomeIcon icon={faDownload} onClick={handleDownloadClick} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasDownloadPermission: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  isOwner: isCurrentUserOwner(state),
});

export default connect(mapStateToProps)(FilterItem);
